// ─── Shared analytics for kanarix-app (client portal + admin) ──────────────────
//
// This app is the SECOND half of the Kanarix funnel (the kanarix.es landing is the
// first). We deliberately report to the SAME Umami / Clarity / GA4 / Meta projects as
// the landing and differentiate the two halves by EVENT NAME: landing events are
// things like `Lead` / `pricing_select`; this app prefixes custom events with `app_`
// (e.g. `app_register_view`, `app_payment_complete`). Standard Meta events
// (CompleteRegistration, InitiateCheckout, Purchase) are also fired so ad campaigns
// can optimize on the real, bottom-of-funnel conversions.
//
// Consent: Umami is cookieless / GDPR-friendly and loads for everyone. GA4, Google
// Ads, Meta Pixel and Clarity set cookies, so they only load (and their events only
// fire) once the visitor accepts via the cookie banner (see lib/consent + CookieConsent).

import { hasAnalyticsConsent, CONSENT_EVENT } from './consent';
import { META_PIXEL_ID } from '../config/analytics';

const UMAMI_SRC = (import.meta.env.VITE_UMAMI_SRC as string | undefined) || 'https://cloud.umami.is/script.js';
const UMAMI_WEBSITE_ID = (import.meta.env.VITE_UMAMI_WEBSITE_ID as string | undefined) || 'd67db35b-9ef3-496c-9512-285493130adb';
const GA4_ID = (import.meta.env.VITE_GA4_ID as string | undefined) || '';
const GOOGLE_ADS_ID = (import.meta.env.VITE_GOOGLE_ADS_ID as string | undefined) || '';
const CLARITY_ID = (import.meta.env.VITE_CLARITY_ID as string | undefined) || 'xbm3yiu4vl';
const FB_PIXEL_ID = META_PIXEL_ID;

declare global {
  interface Window {
    umami?: { track: (name: string, data?: Record<string, unknown>) => void };
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
    clarity?: (...args: unknown[]) => void;
    fbq?: ((...args: unknown[]) => void) & { callMethod?: (...args: unknown[]) => void; queue: unknown[] };
    _fbq?: unknown;
    __PRERENDER__?: boolean;
  }
}

// ─── Exclusions ────────────────────────────────────────────────────────────────
// (1) The logged-in client portal (/portal/*) is a private work area for owners /
//     agencies — no product analytics (Umami) or session recording (Clarity) there.
// (2) Self-exclusion: `?notrack=1` (once) sets a localStorage flag that turns OFF
//     every tracker for this browser (own/dev traffic). `?notrack=0` clears it.
const NOTRACK_KEY = 'kx_notrack';

function isPortal(): boolean {
  return typeof location !== 'undefined' && location.pathname.startsWith('/portal');
}
function selfExcluded(): boolean {
  try { return localStorage.getItem(NOTRACK_KEY) === '1'; } catch { return false; }
}
function applyNotrackParam(): void {
  try {
    const v = new URLSearchParams(location.search).get('notrack');
    if (v === '1') { localStorage.setItem(NOTRACK_KEY, '1'); localStorage.setItem('umami.disabled', '1'); }
    else if (v === '0') { localStorage.removeItem(NOTRACK_KEY); localStorage.removeItem('umami.disabled'); }
  } catch { /* ignore */ }
}

// ─── Loaders ──────────────────────────────────────────────────────────────────
function loadUmami() {
  if (!UMAMI_WEBSITE_ID || document.getElementById('umami-script')) return;
  const s = document.createElement('script');
  s.id = 'umami-script';
  s.async = true;
  s.defer = true;
  s.src = UMAMI_SRC;
  s.setAttribute('data-website-id', UMAMI_WEBSITE_ID);
  document.head.appendChild(s);
}

function loadGoogleTag() {
  const primary = GA4_ID || GOOGLE_ADS_ID;
  if (!primary || document.getElementById('gtag-script')) return;
  const s = document.createElement('script');
  s.id = 'gtag-script';
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${primary}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function (...args: unknown[]) { window.dataLayer!.push(args); };
  window.gtag('js', new Date());
  if (GA4_ID) window.gtag('config', GA4_ID, { send_page_view: false }); // SPA: manual page_view
  if (GOOGLE_ADS_ID) window.gtag('config', GOOGLE_ADS_ID);
}

function loadClarity() {
  if (!CLARITY_ID || document.getElementById('clarity-script')) return;
  const s = document.createElement('script');
  s.id = 'clarity-script';
  s.type = 'text/javascript';
  s.innerHTML =
    `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};` +
    `t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;` +
    `y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script","${CLARITY_ID}");`;
  document.head.appendChild(s);
}

function loadFacebookPixel() {
  if (!FB_PIXEL_ID || document.getElementById('fb-pixel-script')) return;
  window.fbq = function (...args: unknown[]) {
    if (window.fbq!.callMethod) window.fbq!.callMethod(...args);
    else window.fbq!.queue.push(args);
  } as typeof window.fbq;
  if (!window._fbq) window._fbq = window.fbq;
  window.fbq!.queue = [];
  const s = document.createElement('script');
  s.id = 'fb-pixel-script';
  s.async = true;
  s.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(s);
  window.fbq!('init', FB_PIXEL_ID);
  window.fbq!('track', 'PageView');
}

function loadConsentedTrackers() {
  if (selfExcluded()) return;
  loadGoogleTag();
  if (!isPortal()) loadClarity(); // no session recording inside the client portal
  loadFacebookPixel();
}

let started = false;
export function initAnalytics() {
  if (started || typeof window === 'undefined' || window.__PRERENDER__) return;
  started = true;
  applyNotrackParam();
  if (selfExcluded()) return;          // own/dev traffic — no trackers at all
  if (!isPortal()) loadUmami();        // cookieless, but not inside the client portal
  if (hasAnalyticsConsent()) loadConsentedTrackers();
  window.addEventListener(CONSENT_EVENT, (e) => {
    if ((e as CustomEvent).detail === 'accepted') loadConsentedTrackers();
  });
}

// ─── Tracking helpers ───────────────────────────────────────────────────────────

/** Fire a named funnel/engagement event. Umami always; cookie-trackers if consented. */
export function track(event: string, data: Record<string, unknown> = {}) {
  if (typeof window === 'undefined' || selfExcluded()) return;
  if (window.umami && !isPortal()) try { window.umami.track(event, data); } catch { /* ignore */ }
  if (!hasAnalyticsConsent()) return;
  if (window.gtag) try { window.gtag('event', event, data); } catch { /* ignore */ }
  if (window.clarity && !isPortal()) try { window.clarity('event', event); } catch { /* ignore */ }
}

function newEventId(): string {
  return globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}

function getCookie(name: string): string {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1] ?? '') : '';
}

/**
 * Fire a conversion: the `app_` custom event (for Umami/GA4) plus, when given, a
 * STANDARD Meta event (e.g. 'Purchase', 'CompleteRegistration', 'InitiateCheckout').
 * The Meta event is sent via the browser Pixel AND mirrored to the server-side
 * Conversions API (/api/capi) with a shared eventID so Meta deduplicates them — this
 * keeps attribution alive when the Pixel is blocked. CAPI is a no-op until
 * META_PIXEL_ID + META_CAPI_ACCESS_TOKEN are configured on Cloudflare Pages.
 */
/** Raw (un-hashed) PII the CAPI endpoint SHA-256s before sending to Meta. */
export interface ConversionUserData {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
}

export function trackConversion(
  appEvent: string,
  params: Record<string, unknown> = {},
  fbStandardEvent?: string,
  opts?: { eventID?: string; userData?: ConversionUserData },
) {
  track(appEvent, params);
  if (selfExcluded() || !hasAnalyticsConsent() || !fbStandardEvent) return;

  // A caller-supplied eventID lets the browser event dedup against a server event
  // that derives the SAME id (e.g. `purchase_<stripeSessionId>` from the webhook).
  const eventID = opts?.eventID || newEventId();
  if (typeof window.fbq === 'function') {
    try { window.fbq('track', fbStandardEvent, params, { eventID }); } catch { /* ignore */ }
  }
  // Server-side CAPI mirror (deduplicated against the Pixel via eventID).
  try {
    fetch('/api/capi', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        eventName: fbStandardEvent,
        eventID,
        params,
        userData: opts?.userData,
        url: window.location.href,
        fbp: getCookie('_fbp'),
        fbc: getCookie('_fbc'),
      }),
    }).catch(() => {});
  } catch { /* ignore */ }
}

/** Record a GA4 SPA page_view (Umami auto-tracks pageviews from its own script). */
export function trackPageview(path: string) {
  if (typeof window === 'undefined' || selfExcluded() || !hasAnalyticsConsent()) return;
  if (window.gtag && GA4_ID) try { window.gtag('event', 'page_view', { page_path: path }); } catch { /* ignore */ }
}

/** Fire a funnel-stage event at most once per browser session (dedup by key). */
export function trackOnce(event: string, data: Record<string, unknown> = {}) {
  try {
    const k = `kx_evt_${event}`;
    if (sessionStorage.getItem(k)) return;
    sessionStorage.setItem(k, '1');
  } catch { /* ignore */ }
  track(event, data);
}
